import { AdProductSelectionConnected } from '@seek/hirer-ad-products-selection';
import { useTranslations } from '@vocab/react';
import { Heading } from 'braid-design-system';
import React, {
  type ComponentProps,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { TEALIUM_AD_TYPE } from 'src/app/shared/constants/dispatcherId';
import {
  PRODUCT_SET_OK,
  PRODUCT_SET_FAILED_UNAVAILABLE,
  PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE,
} from 'src/app/shared/constants/productSetStatus';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import tealiumDispatch from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateSelectedProductId } from 'src/app/v1/state/modules/formData/selectedProductId';
import { actionResetBuyUpSell } from 'src/app/v1/state/modules/session/buyUpSell';
import { actionRemoveCartLineItem } from 'src/app/v1/state/modules/session/cart';
import { actionUpdateProductSetStatus } from 'src/app/v1/state/modules/session/productSetStatus';
import { actionResetHasCoverImage } from 'src/app/v1/state/modules/tealium/hasCoverImage';
import { actionResetHasLogo } from 'src/app/v1/state/modules/tealium/hasLogo';
import { actionUpdateProductPricing } from 'src/app/v1/state/modules/tealium/productPricing';
import { actionUpdateProductSetProductIds } from 'src/app/v1/state/modules/tealium/productSetProductIds';
import { AdSelectionErrorPage } from 'src/app/v1/views/components/AdSelectionWrapper/AdSelectionErrorPage';
import { useCallbacksForCreateProductSetForAdPosting } from 'src/app/v1/views/hooks/useCallbacksForCreateProductSetForAdPosting';
import { useCreateProductSet } from 'src/app/v1/views/hooks/useCreateProductSet';
import { useHubbleTrackEventHandler } from 'src/app/v1/views/hooks/useHubbleTrackEventHandler';
import type {
  AdProduct,
  CatalogueProductFeature,
  Maybe,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

type AdSelectionWrapperProps = {
  onAdProductSelected?: (adType: string) => void;
};

export const AdSelectionWrapper = ({
  onAdProductSelected,
}: AdSelectionWrapperProps) => {
  const {
    advertiserId,
    advertiserNation,
    draftId,
    jobLocationId,
    jobTitle,
    productSet,
    productSetStatus,
    subClassificationId,
    cart,
    currentSelectedProductId,
    jobLocationCountryCode,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.seekAdvertiserId,
      advertiserNation: state.session.hirerAccount.nation,
      draftId: state.session.draftId,
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      productSet: state.session.productSet,
      productSetStatus: state.session.productSetStatus,
      subClassificationId: state.session.seekSubClassificationId,
      cart: state.session.cart,
      currentSelectedProductId: state.formData.selectedProductId,
      jobLocationCountryCode: state.session.jobLocationCountry,
    }),
    shallowEqual,
  );
  const { t } = useTranslations(translations);

  const dispatch = useDispatch();
  const { getHubbleTrackEventHandler } = useHubbleTrackEventHandler();

  const getSelectedProductFromProductSet = useCallback(
    (selectedProduct: string) =>
      (productSet?.items as AdProduct[]).find(
        (item) => item.id === selectedProduct,
      ),
    [productSet],
  );

  const updateCart = useCallback(
    (selectedAdProductId: string) => {
      if (
        currentSelectedProductId &&
        currentSelectedProductId !== selectedAdProductId
      ) {
        dispatch(actionRemoveCartLineItem(currentSelectedProductId));
        // clear budget too if contains selected budget
        const upsellBudget = cart?.lineItems.find(
          (item) => item.type === 'Budget',
        );
        if (upsellBudget) {
          dispatch(actionRemoveCartLineItem(upsellBudget.productId));
        }
      }
    },
    [cart, currentSelectedProductId, dispatch],
  );

  const resetBrandingIfFeatureExists = useCallback(
    (features: Maybe<CatalogueProductFeature>[] | undefined) => {
      if (features) {
        const brandingFeature = features.find(
          (feature) => feature?.type === 'Branding',
        );
        if (!brandingFeature) {
          dispatch(actionResetHasCoverImage());
          dispatch(actionResetHasLogo());
        }
      }
    },
    [dispatch],
  );

  const updateAdTypeAndResetProductSetStatus = useCallback(
    (selectedProduct?: AdProduct): void => {
      if (selectedProduct) {
        dispatch(
          tealiumDispatch(
            TEALIUM_AD_TYPE,
            selectedProduct.displayMetadata?.name || selectedProduct.name,
          ),
        );

        if (productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE) {
          dispatch(actionUpdateProductSetStatus(PRODUCT_SET_OK));
        }
      } else {
        log.warn(
          'AdSelectionWrapper | handleAdProductUpdate | No selected product id found',
          {
            advertiserId,
            advertiserNation,
            selectedProduct,
            productSet,
          },
        );
      }
    },
    [advertiserId, advertiserNation, productSet, dispatch, productSetStatus],
  );

  const handleAdProductUpdate = useCallback(
    async ({ selectedAdProductId }: { selectedAdProductId: string }) => {
      dispatch(actionUpdateSelectedProductId(selectedAdProductId));
      const selectedProduct =
        getSelectedProductFromProductSet(selectedAdProductId);
      updateAdTypeAndResetProductSetStatus(selectedProduct);
      updateCart(selectedAdProductId);
      resetBrandingIfFeatureExists(selectedProduct?.features);
      onAdProductSelected?.(selectedAdProductId);
      // Reset BuyUpSell flag used on checkout page to select premium upsell
      dispatch(actionResetBuyUpSell());
    },
    [
      dispatch,
      onAdProductSelected,
      getSelectedProductFromProductSet,
      updateCart,
      resetBrandingIfFeatureExists,
      updateAdTypeAndResetProductSetStatus,
    ],
  );

  const onAdProductSelectionSelected = useCallback<
    NonNullable<
      ComponentProps<typeof AdProductSelectionConnected>['onAdProductSelected']
    >
  >(
    (productId) => handleAdProductUpdate({ selectedAdProductId: productId }),
    [handleAdProductUpdate],
  );

  const productSetCallbacks = useCallbacksForCreateProductSetForAdPosting();
  const createProductSet = useCreateProductSet(productSetCallbacks);

  const adProductErrorHandler = useCallback(
    async (error: Error) => {
      const retryableErrorNames = ['ProductSetError'];

      log.error(
        'AdSelectionWrapper | HirerAdSelection | onError',
        {
          error,
          advertiserId,
          advertiserNation,
          jobAdDetails: {
            locationId: jobLocationId.toString(),
            roleTitle: jobTitle,
            subClassificationId,
          },
          draftId,
        },
        true,
      );

      if (
        retryableErrorNames.find(
          (retryableErrorName) => retryableErrorName === error.name,
        )
      ) {
        await createProductSet();
      }
    },
    [
      advertiserId,
      advertiserNation,
      createProductSet,
      draftId,
      jobLocationId,
      jobTitle,
      subClassificationId,
    ],
  );

  const onTrackingCallback = useCallback(
    (eventData: unknown) => {
      const adSelectionEventData = eventData as TrackingEvent;

      if (adSelectionEventData.productPricing) {
        dispatch(
          actionUpdateProductPricing(adSelectionEventData.productPricing),
        );
      }
      if (adSelectionEventData.productSetProductIds) {
        dispatch(
          actionUpdateProductSetProductIds(
            adSelectionEventData.productSetProductIds,
          ),
        );
      }
      getTrackingAdapter.sendLinkEvent(adSelectionEventData);
    },
    [dispatch],
  );

  const [productSetCreationRetried, setProductSetCreationRetried] =
    useState<boolean>(false);

  useEffect(() => {
    if (!productSet) {
      createProductSet().finally(() => {
        setProductSetCreationRetried(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // disabling the lint warning since we want to use [] to have this useEffect to be executed only at the loading time

  if (
    productSetCreationRetried &&
    productSetStatus === PRODUCT_SET_FAILED_UNAVAILABLE
  ) {
    log.error('AdSelection | Failed to create product set', {
      advertiserId,
      jobLocationId,
      jobTitle,
      subClassificationId,
    });
    return <AdSelectionErrorPage />;
  }

  if (!productSet) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<AdSelectionErrorPage />}>
      <AdProductSelectionConnected
        onAdProductSelected={onAdProductSelectionSelected}
        onError={adProductErrorHandler}
        context={{
          hirer: { id: advertiserId },
          products: { productSetId: productSet.id },
          draft: draftId ? { id: draftId } : undefined,
          job: {
            location: {
              id: jobLocationId,
              country: { code: jobLocationCountryCode?.toUpperCase() },
            },
          },
        }}
        selectedProductId={currentSelectedProductId}
        errorComponent={<AdSelectionErrorPage />}
        layout={{
          align: 'auto',
          prefixContent: {
            products: <Heading level="2">{t('Select an ad type')}</Heading>,
          },
        }}
        onTrackingEvent={(payload) => {
          if (payload.tracking) {
            onTrackingCallback(payload.tracking);
          }
          if (payload.hubble) {
            getHubbleTrackEventHandler()(
              payload.hubble.eventName,
              payload.hubble.eventData,
            );
          }
        }}
      />
    </ErrorBoundary>
  );
};
